import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';

import { AppConfigLoader } from '../../../config/app-config.loader';

export interface UserConsentItem {
  id: string;
  versionId: string;
  optional: boolean;
  documentUrl: string;
  name: string;
  consented: boolean;
}

export interface ConsentUpdatePayload {
  id: string;
  versionId: string;
  consented: boolean;
}

export type UserConsentsResponse = Array<UserConsentItem>;

/**
 * HTTP adapter for the consent management backend (terms-service).
 * Only responsible for REST payload/response shaping — no business logic.
 */
@Injectable({
  providedIn: 'root',
})
export class ConsentRestService {
  // services
  private readonly http = inject(HttpClient);
  private readonly appConfigLoader = inject(AppConfigLoader);

  private get baseUrl(): string {
    return `${this.appConfigLoader.activeConfig().backend.termsApiUrl}/consent/user`;
  }

  /** Loads all consents for the current user (given and possible). */
  loadUserConsents$(language: string): Observable<UserConsentsResponse> {
    return this.http.get<UserConsentsResponse>(`${this.baseUrl}/all`, { params: { lang: language } });
  }

  /** Loads only the consents the user has NOT yet given. */
  loadMissingConsents$(language: string): Observable<UserConsentsResponse> {
    return this.http.get<UserConsentsResponse>(`${this.baseUrl}/missing`, { params: { lang: language } });
  }

  /** Updates the user's consent decisions. */
  updateUserConsents$(consents: Array<ConsentUpdatePayload>, language: string): Observable<unknown> {
    return this.http.post<unknown>(this.baseUrl, consents, { params: { lang: language } });
  }
}
